import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  ShieldCheck, Users, ListChecks, WarningOctagon, ChartBar,
  Export, SignOut, User, CaretDown, GearSix, ClipboardText, FileXls, Plugs, IdentificationCard, Key
} from "@phosphor-icons/react";
import { UsersTab } from "@/pages/admin/UsersTab";
import { ValidationsTab } from "@/pages/admin/ValidationsTab";
import { FlaggedTab } from "@/pages/admin/FlaggedTab";
import { AnalyticsTab } from "@/pages/admin/AnalyticsTab";
import { ExportTab } from "@/pages/admin/ExportTab";
import { SettingsTab } from "@/pages/admin/SettingsTab";
import { AuditLogTab } from "@/pages/admin/AuditLogTab";
import { GuestRegistryTab } from "@/pages/admin/GuestRegistryTab";
import { InterfaceSetupTab } from "@/pages/admin/InterfaceSetupTab";
import { SecurityTab } from "@/pages/admin/SecurityTab";
import { SecurityAdminTab } from "@/pages/admin/SecurityAdminTab";
import { KeyManagementTab } from "@/pages/admin/KeyManagementTab";
import { ProfileTab } from "@/pages/admin/ProfileTab";
import { LastSyncBadge } from "@/components/LastSyncBadge";

function hasPerm(user, perm) {
  if (user?.role === "superadmin") return true;
  const perms = user?.permissions || [];
  if (perms.includes(perm)) return true;
  // 'change_user_password' implies access to the Users tab
  if (perm === "users" && perms.includes("change_user_password")) return true;
  return false;
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();

  const canAnalytics = hasPerm(user, "analytics");
  const canUsers = hasPerm(user, "users");
  const canValidations = hasPerm(user, "validations");
  const canFlagged = hasPerm(user, "flagged");
  const canExport = hasPerm(user, "export");
  const canSettings = hasPerm(user, "settings");
  const canAudit = hasPerm(user, "audit");
  const canGuestRegistry = hasPerm(user, "guest_registry");
  const canInterface = hasPerm(user, "interface");
  const canSecurity = hasPerm(user, "security");
  const canSecurityAdmin = hasPerm(user, "security_admin");
  const canKeyManagement = hasPerm(user, "key_management");

  // Default tab: from URL param or first available
  const urlTab = searchParams.get("tab");
  const defaultTab = urlTab || (canAnalytics ? "analytics" : canValidations ? "validations" : "validations");
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Listen for cross-tab navigation events (e.g., from Audit Log target click)
  useEffect(() => {
    const handler = (e) => {
      const { tab, highlightId } = e.detail || {};
      if (tab) {
        setActiveTab(tab);
        if (highlightId) {
          // Re-dispatch a highlight event after the tab has switched
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent("admin-highlight", { detail: { tab, highlightId } }));
          }, 200);
        }
      }
    };
    window.addEventListener("admin-navigate", handler);
    return () => window.removeEventListener("admin-navigate", handler);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck size={24} weight="bold" className="text-[#007AFF]" />
            <h1 className="font-heading text-lg font-bold text-foreground tracking-tight" data-testid="admin-header-title">
              ADMIN PANEL
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <LastSyncBadge />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="admin-user-menu">
                  <User size={16} />
                  <span className="text-sm">{user?.name}</span>
                  <CaretDown size={12} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card border-border">
                <DropdownMenuItem onClick={() => { window.location.search = "?tab=profile"; }} className="cursor-pointer text-muted-foreground hover:text-foreground" data-testid="admin-profile-btn">
                  <IdentificationCard size={14} className="mr-2" /> My Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} className="text-[#FF3B30] focus:text-[#FF3B30] focus:bg-[#FF3B30]/10 cursor-pointer" data-testid="admin-logout-btn">
                  <SignOut size={16} className="mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-card border border-border rounded-sm h-auto p-1 flex-wrap" data-testid="admin-tabs">
            {canAnalytics && (
              <TabsTrigger value="analytics" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-analytics">
                <ChartBar size={14} className="mr-1.5" /> Analytics
              </TabsTrigger>
            )}
            {canUsers && (
              <TabsTrigger value="users" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-users">
                <Users size={14} className="mr-1.5" /> Users
              </TabsTrigger>
            )}
            {canValidations && (
              <TabsTrigger value="validations" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-validations">
                <ListChecks size={14} className="mr-1.5" /> Validations
              </TabsTrigger>
            )}
            {canFlagged && (
              <TabsTrigger value="flagged" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-flagged">
                <WarningOctagon size={14} className="mr-1.5" /> Flagged
              </TabsTrigger>
            )}
            {canExport && (
              <TabsTrigger value="export" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-export">
                <Export size={14} className="mr-1.5" /> Export
              </TabsTrigger>
            )}
            {canSettings && (
              <TabsTrigger value="settings" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-settings">
                <GearSix size={14} className="mr-1.5" /> Settings
              </TabsTrigger>
            )}
            {canAudit && (
              <TabsTrigger value="audit" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-audit">
                <ClipboardText size={14} className="mr-1.5" /> Audit Log
              </TabsTrigger>
            )}
            {canGuestRegistry && (
              <TabsTrigger value="guest-registry" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-guest-registry">
                <FileXls size={14} className="mr-1.5" /> Guest Registry
              </TabsTrigger>
            )}
            {canInterface && (
              <TabsTrigger value="interface" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-interface">
                <Plugs size={14} className="mr-1.5" /> Interface
              </TabsTrigger>
            )}
            {canSecurity && (
              <TabsTrigger value="security" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-security">
                <ShieldCheck size={14} className="mr-1.5" /> Security
              </TabsTrigger>
            )}
            {canSecurityAdmin && (
              <TabsTrigger value="security-admin" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-security-admin">
                <ShieldCheck size={14} className="mr-1.5" /> Security Admin
              </TabsTrigger>
            )}
            {canKeyManagement && (
              <TabsTrigger value="key-management" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-key-management">
                <Key size={14} className="mr-1.5" /> Key Management
              </TabsTrigger>
            )}
            <TabsTrigger value="profile" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#007AFF] data-[state=active]:text-white" data-testid="tab-profile">
              <IdentificationCard size={14} className="mr-1.5" /> Profile
            </TabsTrigger>
          </TabsList>

          {canAnalytics && <TabsContent value="analytics"><AnalyticsTab /></TabsContent>}
          {canUsers && <TabsContent value="users"><UsersTab /></TabsContent>}
          {canValidations && <TabsContent value="validations"><ValidationsTab /></TabsContent>}
          {canFlagged && <TabsContent value="flagged"><FlaggedTab /></TabsContent>}
          {canExport && <TabsContent value="export"><ExportTab /></TabsContent>}
          {canSettings && <TabsContent value="settings"><SettingsTab /></TabsContent>}
          {canAudit && <TabsContent value="audit"><AuditLogTab /></TabsContent>}
          {canGuestRegistry && <TabsContent value="guest-registry"><GuestRegistryTab /></TabsContent>}
          {canInterface && <TabsContent value="interface"><InterfaceSetupTab /></TabsContent>}
          {canSecurity && <TabsContent value="security"><SecurityTab /></TabsContent>}
          {canSecurityAdmin && <TabsContent value="security-admin"><SecurityAdminTab /></TabsContent>}
          {canKeyManagement && <TabsContent value="key-management"><KeyManagementTab /></TabsContent>}
          <TabsContent value="profile"><ProfileTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
